/**
 * Urgency scoring for budget-optimised allocation (Pass 1 sort order).
 * Weights: 60% stock coverage, 30% velocity, 10% cost. All scores [0, 10].
 * See README.md § "Urgency scoring" for the full scoring table and rationale.
 */

/**
 * Stock coverage score based on Months of Stock (MOS = soh / amc).
 * amc=0 → MOS=Infinity → score=0 (unknown urgency, ranked by velocity/cost).
 *
 * @param {number} soh  Stock on hand in generic units
 * @param {number} amc  Average monthly consumption in generic units/month
 * @returns {{ mos: number, stockScore: number }}
 */
function getStockScore (soh, amc) {
  const mos = amc > 0 ? soh / amc : Infinity

  const stockScore =
    mos === 0 ? 10 // stockout — critical, no stock at all
      : mos < 1 ? 8 // below 1 month — very high risk
        : mos < 2 ? 5 // below 2 months — high risk
          : mos < 3 ? 2 // below 3 months — moderate risk
            : 0 // 3+ months — low urgency this cycle

  return { mos, stockScore }
}

/**
 * Velocity score relative to facility-wide avgAmc.
 * >2× avg=10 (fast), 0.5–2× avg=5 (normal), <0.5× avg=1 (slow).
 * avgAmc=0 → neutral score=5 (no reference available).
 *
 * @param {number} amc     Product average monthly consumption
 * @param {number} avgAmc  Facility-wide average AMC across all products in scope
 * @returns {{ velocityRatio?: number, velocityLabel: string, velocityScore: number }}
 */
function getVelocityScore (amc, avgAmc) {
  if (avgAmc <= 0) {
    // No facility-wide average to compare against — neutral score
    return { velocityLabel: 'no-data', velocityScore: 5 }
  }

  const velocityRatio = amc / avgAmc

  const velocityLabel =
    velocityRatio > 2 ? 'fast-moving (>2×avg)'
      : velocityRatio >= 0.5 ? 'normal (0.5–2×avg)'
        : 'slow-moving (<0.5×avg)'

  const velocityScore =
    velocityRatio > 2 ? 10
      : velocityRatio >= 0.5 ? 5
        : 1

  return { velocityRatio, velocityLabel, velocityScore }
}

/**
 * Cost efficiency score: (1 − costPerUnit / maxCostPerUnit) × 10.
 * maxCostPerUnit=0 → neutral score=5 (no reference available).
 *
 * @param {number} costPerUnit     sellPrice / genericFactor for this product
 * @param {number} maxCostPerUnit  Highest CPU across all products in scope
 * @returns {number} costScore in [0, 10]
 *
 * @example
 * getCostScore(5, 10)   // → 5    (midpoint)
 * getCostScore(10, 10)  // → 0    (most expensive)
 * getCostScore(0, 10)   // → 10   (cheapest)
 */
function getCostScore (costPerUnit, maxCostPerUnit) {
  if (maxCostPerUnit <= 0) return 5 // no cost reference — neutral
  return (1 - costPerUnit / maxCostPerUnit) * 10
}

/**
 * Combined urgency score: 0.6×stock + 0.3×velocity + 0.1×cost. Range [0, 10].
 *
 * @param {{ soh: number, amc: number, sellPrice: number, genericFactor: number }} product
 * @param {{ avgAmc: number, maxCostPerUnit: number }} normalisers
 * @returns {number} urgencyScore — higher means more urgent
 */
function calculateUrgencyScore (product, { avgAmc, maxCostPerUnit }) {
  const { soh, amc, sellPrice, genericFactor } = product
  const costPerUnit = sellPrice / genericFactor

  const { stockScore } = getStockScore(soh, amc)
  const { velocityScore } = getVelocityScore(amc, avgAmc)
  const costScore = getCostScore(costPerUnit, maxCostPerUnit)

  return (0.6 * stockScore) + (0.3 * velocityScore) + (0.1 * costScore)
}

/**
 * Scores every input and returns them sorted by urgency descending.
 * Initialises `genericUnitsAllocated: 0` on each item for the allocation loop.
 *
 * @param {Array<object>} inputs  Output of buildAlgorithmInput()
 * @param {{ avgAmc: number, maxCostPerUnit: number }} normalisers
 * @returns {Array<object & { urgencyScore: number, genericUnitsAllocated: 0 }>}
 *
 */
function scoreAndSortInputs (inputs, { avgAmc, maxCostPerUnit }) {
  return inputs
    .map(item => ({
      ...item,
      urgencyScore: calculateUrgencyScore(item, { avgAmc, maxCostPerUnit }),
      genericUnitsAllocated: 0
    }))
    .sort((a, b) => b.urgencyScore - a.urgencyScore)
}

exports.getStockScore = getStockScore
exports.getVelocityScore = getVelocityScore
exports.getCostScore = getCostScore
exports.calculateUrgencyScore = calculateUrgencyScore
exports.scoreAndSortInputs = scoreAndSortInputs
